import React from 'react';
import { createPortal } from 'react-dom';

import { GamePlayerStatus } from '../../../../Constants';
import { getCardTextureUrl } from '../../Common/TextureTools';
import FireIcon from '../../Common/FireIcon';

import '../draft-choice.css';
import './DraftOverlay.css';


// Mirror FateDieOverlay's auto-mode probe (FateDieOverlay.js lines
// 188-198): when the seated player has engaged auto-play, suppress the
// ceremony overlay entirely so headless / auto playthroughs stay fast.
function detectAutoMode(app) {
    try {
        if (app && app.game
                && typeof app.game.automaticPlayerIsEngaged === 'function') {
            return !!app.game.automaticPlayerIsEngaged();
        }
    } catch {
        return false;
    }
    return false;
}


// The active drafter is the first seat sitting at PICK_DRAFT_ID. Backend
// status drives the overlay: when that seat exits PICK_DRAFT_ID, every
// client's next gameData push flips the trigger and the overlay unmounts
// — no watchdog, no event ack relay needed.
function findDrafter(gameData) {
    if (!gameData || !Array.isArray(gameData.gameplayer_set)) return null;
    return gameData.gameplayer_set.find(
        (p) => p && p.status === GamePlayerStatus.PICK_DRAFT_ID) || null;
}


// Windowed Draft ceremony. Unlike the mobile DraftSelector (which renders
// inline only for the active drafter), this mounts for every seated
// client and portals a full-screen overlay so observers see the two
// candidate cards while one seat picks.
//
// Layout is a plain flex container (not Swiper): on narrow viewports the
// two landscape guest cards stack vertically; at >= 500px (the canonical
// TwoD mobile/desktop breakpoint, set in game-player.css line 18) the
// container flips to row and the cards sit side-by-side. Swiper with
// slidesPerView=2 is horizontal-only and is the wrong container for a
// responsive direction flip; the verbatim mirror with DraftSelector
// covers the dashed-border highlight + the api call destination, not the
// container around them.
export default function DraftOverlay({ app, gameData, gamePlayerData }) {
    const isAuto = detectAutoMode(app);
    const draftChoices = (gameData && Array.isArray(gameData.draft_choices))
        ? gameData.draft_choices : [];
    const drafter = findDrafter(gameData);

    // Trigger: cards in flight AND some seat is drafting. Auto-mode and
    // the empty / no-drafter states all bail before the portal.
    if (isAuto) return null;
    if (draftChoices.length === 0) return null;
    if (!drafter) return null;

    const isDrafter = !!gamePlayerData
        && gamePlayerData.status === GamePlayerStatus.PICK_DRAFT_ID;

    // Seat number for the observer caption — seat + 1 to match how
    // FateDieOverlay formats roller seats (FateDieOverlay.js line 488).
    const drafterSeatNumber =
        (drafter.seat !== undefined && drafter.seat !== null)
            ? drafter.seat + 1 : null;

    let headline;
    if (isDrafter) {
        headline = 'Pick a guest.';
    } else if (drafterSeatNumber !== null) {
        headline = `Player ${drafterSeatNumber} is drafting…`;
    } else {
        headline = 'Waiting for the draft…';
    }

    // Per-card click handler: same destination as DraftSelector line 23,
    // routed directly per-card instead of via swiper.clickedIndex.
    const handleCardClick = (cardId) => {
        app.game.seatedController.gamePlayerApi.clickDraftChoice(cardId);
    };

    const cardsClasses = ['draft-overlay__cards'];
    if (!isDrafter) cardsClasses.push('draft-overlay__cards--locked');

    const overlay = (
        <div
            className={'draft-overlay'}
            role={'status'}
            aria-live={'polite'}
            aria-label={'Guest draft'}
            data-testid={'draft-overlay'}
        >
            <div className={'draft-overlay__scrim'}/>
            <div className={'draft-overlay__stage'}>
                <div className={'draft-overlay__caption'}>{headline}</div>
                <p
                    className={'draft-overlay__manual'}
                    data-testid={'draft-overlay-manual'}
                >
                    As Player One, you draw 2 Guest cards, pick one to
                    place in Orbit Column 1, and burn the other.
                </p>
                <div
                    className={cardsClasses.join(' ')}
                    data-testid={'draft-overlay-cards'}
                >
                    {draftChoices.map((cardId, index) => {
                        const cardKey = `draft-card-${index}`;
                        const commonChildren = (
                            <>
                                <div className={'draft-border-dashed'}/>
                                <img
                                    src={getCardTextureUrl(cardId)}
                                    alt="card"
                                    className={'draft-card-img'}
                                />
                                <FireIcon className={'draft-will-burn'}/>
                            </>
                        );
                        if (isDrafter) {
                            return (
                                <button
                                    key={cardKey}
                                    type={'button'}
                                    className={'draft-overlay__card'}
                                    data-testid={'draft-overlay-card'}
                                    data-card-id={cardId}
                                    onClick={() => handleCardClick(cardId)}
                                >
                                    {commonChildren}
                                </button>
                            );
                        }
                        return (
                            <div
                                key={cardKey}
                                className={'draft-overlay__card'}
                                data-testid={'draft-overlay-card'}
                                data-card-id={cardId}
                            >
                                {commonChildren}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    if (typeof document === 'undefined' || !document.body) return null;
    return createPortal(overlay, document.body);
}
